import type { FC } from 'react';
import {
  Button, Checkbox, Form, FormGroup, InlineLoading, InlineNotification, Link, Stack,
} from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';

import { PaginaDeAuth } from '../../../padroes/PaginaDeAuth';
import { CampoDeNascimento } from '../CampoDeNascimento';
import { ROTULOS, rotuloDoAviso } from '../../../padroes/rotulos';
import styles from '../auth.module.scss';

// MAIORIDADE E ACEITE (LGPD) — o mesmo padrão das telas de entrada: a data de nascimento (quando
// o servidor ainda não tem), as declarações num `FormGroup` de `Checkbox`es (nenhuma nasce
// marcada; comunicações é opcional e separada), o primário só depois dos três aceites, e sair.

export interface PropsDoConsentimento {
  precisaData: boolean;
  soReaceite: boolean;
  birthDate: string; setBirthDate: (v: string) => void;
  maioridade: boolean; setMaioridade: (v: boolean) => void;
  termos: boolean; setTermos: (v: boolean) => void;
  politica: boolean; setPolitica: (v: boolean) => void;
  comunicacoes: boolean; setComunicacoes: (v: boolean) => void;
  dataInvalida: boolean;
  dataFutura: boolean;
  menorDeIdade: boolean;
  podeEnviar: boolean;
  loading: boolean;
  error: string | null;
  enviar: () => void;
  sair: () => void;
  idadeMinima: number;
}

export const Consentimento: FC<PropsDoConsentimento> = (p) => {
  const invalido = p.dataInvalida
    ? 'Essa data não existe no calendário.'
    : p.dataFutura ? 'A data não pode estar no futuro.' : undefined;

  return (
    <PaginaDeAuth
      titulo={p.soReaceite ? 'Atualizamos nossos documentos' : 'Confirme sua idade e o aceite dos termos'}
      apoio={p.soReaceite
        ? 'Revise e aceite a versão mais recente para seguir usando a Maestra.'
        : `A Maestra é destinada a maiores de ${p.idadeMinima} anos. Precisamos registrar sua declaração e o aceite dos nossos documentos, como exige a Lei Geral de Proteção de Dados.`}
    >
      <Form noValidate onSubmit={(e) => { e.preventDefault(); p.enviar(); }}>
        <Stack gap={7}>
          {p.precisaData && (
            <Stack gap={5}>
              <CampoDeNascimento
                id='consentimento-nascimento'
                valor={p.birthDate}
                aoMudar={p.setBirthDate}
                idadeMinima={p.idadeMinima}
                invalido={invalido}
              />
              {p.menorDeIdade && (
                <InlineNotification
                  kind='warning'
                  lowContrast
                  hideCloseButton
                  title={`A Maestra é destinada a maiores de ${p.idadeMinima} anos.`}
                  subtitle='Ao enviar, sua conta ficará bloqueada para revisão.'
                  statusIconDescription={rotuloDoAviso('warning')}
                  aria-label={ROTULOS.fecharAviso}
                />
              )}
            </Stack>
          )}

          <FormGroup legendText='Declarações e aceites'>
            <Stack gap={4}>
              <Checkbox
                id='consentimento-maioridade'
                checked={p.maioridade}
                onChange={(_, { checked }) => p.setMaioridade(checked)}
                labelText={`Declaro ter ${p.idadeMinima} anos ou mais.`}
              />
              <Checkbox
                id='consentimento-termos'
                checked={p.termos}
                onChange={(_, { checked }) => p.setTermos(checked)}
                labelText={<>Li e concordo com os <Link href='/legal/termos' target='_blank' rel='noreferrer' inline>Termos de Uso</Link>.</>}
              />
              <Checkbox
                id='consentimento-politica'
                checked={p.politica}
                onChange={(_, { checked }) => p.setPolitica(checked)}
                labelText={<>Li e concordo com a <Link href='/legal/privacidade' target='_blank' rel='noreferrer' inline>Política de Privacidade</Link>.</>}
              />
              <Checkbox
                id='consentimento-comunicacoes'
                checked={p.comunicacoes}
                onChange={(_, { checked }) => p.setComunicacoes(checked)}
                labelText='Quero receber novidades da Maestra por e-mail (opcional; dá para cancelar quando quiser).'
              />
            </Stack>
          </FormGroup>

          {p.error && <InlineNotification kind='error' lowContrast hideCloseButton title={p.error} statusIconDescription={rotuloDoAviso('error')} aria-label={ROTULOS.fecharAviso} />}

          <div className={styles.acoes}>
            {p.loading
              ? <InlineLoading description='Registrando…' iconDescription={ROTULOS.carregando} />
              : <Button type='submit' renderIcon={ArrowRight} disabled={!p.podeEnviar}>Confirmar e continuar</Button>}
            <Button kind='ghost' onClick={p.sair}>Sair da conta</Button>
          </div>
        </Stack>
      </Form>
    </PaginaDeAuth>
  );
};
